// One-shot "show my location": a viewer-local dot and accuracy halo that clear
// themselves after ten minutes. The position is never written to map state, so
// it cannot reach the URL, an export, or a render.png.

// @deno-types="npm:@types/leaflet@^1.9.12"
import type * as Leaflet from "leaflet";
// @deno-types="npm:@types/mithril@^2.2.7"
import m from "mithril";
import { leaflet } from "./leaflet.ts";
import { mapContext } from "./context.ts";
import { ui } from "../../ui.ts";

// How long a located dot stays on the map before it disappears.
export const LOCATION_TTL_MS = 10 * 60 * 1000;

// The dot and accuracy halo, kept out of featureLayers so clearing or
// re-rendering the map's own features leaves the location alone.
let layers: Leaflet.LayerGroup | undefined;
let expiryTimer: number | undefined;
// Identifies the latest lookup, so a slower earlier callback cannot draw over a
// newer one or after the map has been torn down.
let requestId = 0;

function clearDot(): void {
  if (expiryTimer !== undefined) {
    clearTimeout(expiryTimer);
    expiryTimer = undefined;
  }
  layers?.remove();
  layers = undefined;
}

function expire(): void {
  clearDot();
  ui.locationStatus = "idle";
  ui.locationMessage = "";
  m.redraw();
}

function draw(map: Leaflet.Map, position: GeolocationPosition): void {
  clearDot();
  const { latitude, longitude, accuracy } = position.coords;
  const point: Leaflet.LatLngExpression = [latitude, longitude];
  const group = leaflet.layerGroup().addTo(map);
  layers = group;
  // Accuracy is metres; Leaflet's circle radius is metres too. A zero accuracy
  // still deserves a visible (if tiny) halo.
  leaflet.circle(point, {
    radius: Math.max(accuracy || 0, 1),
    color: "#1a73e8",
    weight: 1,
    fillColor: "#1a73e8",
    fillOpacity: 0.12,
    interactive: false,
    className: "location-accuracy",
  }).addTo(group);
  leaflet.circleMarker(point, {
    radius: 7,
    color: "#fff",
    weight: 2,
    fillColor: "#1a73e8",
    fillOpacity: 1,
    interactive: false,
    className: "location-dot",
  }).addTo(group);
  // Moving to the viewer's position is viewer-local, so it must not overwrite
  // the shared URL's authored viewport. setView is synchronous with animations
  // off, so the guard only covers this call.
  mapContext.suppressViewCapture = true;
  try {
    map.setView(point, Math.max(map.getZoom(), 14), { animate: false });
  } finally {
    mapContext.suppressViewCapture = false;
  }
  expiryTimer = setTimeout(expire, LOCATION_TTL_MS);
}

function errorMessage(error: GeolocationPositionError): string {
  if (error.code === 1) {
    return "Location permission denied";
  }
  if (error.code === 3) {
    return "Location lookup timed out";
  }
  return "Your location is unavailable";
}

// Ask for one fresh position and draw it. A second press replaces the dot and
// restarts the ten-minute lifetime.
export function locate(): void {
  const map = mapContext.map;
  if (!map) {
    return;
  }
  if (!globalThis.navigator.geolocation) {
    ui.locationStatus = "error";
    ui.locationMessage = "This browser cannot show your location";
    m.redraw();
    return;
  }

  // A refreshed lookup supersedes the old dot's timer. Leave the dot visible
  // until the new position arrives, but don't let the old expiry fire mid-lookup
  // and reset the status to idle.
  if (expiryTimer !== undefined) {
    clearTimeout(expiryTimer);
    expiryTimer = undefined;
  }

  const id = ++requestId;
  ui.locationStatus = "locating";
  ui.locationMessage = "";
  m.redraw();

  globalThis.navigator.geolocation.getCurrentPosition(
    (position) => {
      if (id !== requestId || mapContext.map !== map) {
        return;
      }
      draw(map, position);
      ui.locationStatus = "active";
      m.redraw();
    },
    (error) => {
      if (id !== requestId) {
        return;
      }
      clearDot();
      ui.locationStatus = "error";
      ui.locationMessage = errorMessage(error);
      m.redraw();
    },
    { enableHighAccuracy: true, maximumAge: 0, timeout: 10_000 },
  );
}

// Drop the dot and cancel any pending lookup. Called when the map is torn down
// (and by the expiry timer).
export function clearLocation(): void {
  requestId++;
  clearDot();
  ui.locationStatus = "idle";
  ui.locationMessage = "";
}
